import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X } from "lucide-react";

const EMPTY = { name: "", company: "", contact_person: "", email: "", phone: "", state: "", gstin: "", notes: "" };

export default function Clients() {
    const { hasPerm } = useAuth();
    const [items, setItems] = useState([]);
    const [show, setShow] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [err, setErr] = useState("");

    const load = async () => { const { data } = await api.get("/clients"); setItems(data); };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try {
            if (editId) await api.put(`/clients/${editId}`, form); else await api.post("/clients", form);
            setShow(false); setForm(EMPTY); setEditId(null); load();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };

    return (
        <div data-testid="clients-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Master data</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900">Clients</h1>
                </div>
                {hasPerm("client.manage") && <button data-testid="new-client-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShow(true); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"><Plus size={15} /> New client</button>}
            </div>

            {!hasPerm("client.manage") && (
                <div className="bg-stone-100 border border-stone-200 rounded-xl p-4 text-sm text-stone-700">
                    <span className="font-medium">Read-only view.</span> Clients are managed by the Administrator and Full Access users.
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                            <th className="text-left py-3 px-4">Name</th>
                            <th className="text-left py-3 px-4">Company</th>
                            <th className="text-left py-3 px-4">Contact</th>
                            <th className="text-left py-3 px-4">State</th>
                            <th className="text-left py-3 px-4">GSTIN</th>
                            <th className="text-right py-3 px-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(c => (
                            <tr key={c.id} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-4 font-medium text-stone-900">{c.name}</td>
                                <td className="py-3 px-4 text-stone-700">{c.company || "—"}</td>
                                <td className="py-3 px-4 text-stone-700"><div>{c.contact_person}</div><div className="text-xs text-stone-500">{c.email}</div></td>
                                <td className="py-3 px-4 text-stone-700">{c.state || "—"}</td>
                                <td className="py-3 px-4 font-mono text-xs text-stone-600">{c.gstin || "—"}</td>
                                <td className="py-3 px-4 text-right">
                                    {hasPerm("client.manage") && <button onClick={() => { setForm(c); setEditId(c.id); setShow(true); }} className="text-xs text-amber-700 hover:text-amber-900">Edit</button>}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-stone-500 text-sm">No clients yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
                    <div className="bg-white border border-stone-200 rounded-xl max-w-2xl w-full">
                        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                            <div className="font-heading font-semibold text-stone-900">{editId ? "Edit client" : "New client"}</div>
                            <button onClick={() => setShow(false)} className="text-stone-500"><X size={18} /></button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                            <div className="grid md:grid-cols-2 gap-4">
                                {["name","company","contact_person","email","phone","state","gstin"].map(k => (
                                    <div key={k}>
                                        <label className="overline block mb-1 capitalize">{k.replace("_"," ")}{k==="name"&&" *"}</label>
                                        <input data-testid={`c-${k}`} required={k==="name"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label className="overline block mb-1">Notes</label>
                                    <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" rows={2} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShow(false)} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                                <button data-testid="save-client" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
